import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { Options, SeriesGaugeOptions, YAxisPlotBandsOptions } from 'highcharts';
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from 'highcharts-angular';

const DEFAULT_HEIGHT = 100;
const BAND_THICKNESS = 15;
const AXIS_MAX = 100;

@Component({
  selector: 'procure-gauge-chart',
  templateUrl: './gauge-chart.component.html',
  styleUrl: './gauge-chart.component.scss',
  imports: [HighchartsChartComponent],
  providers: [
    providePartialHighcharts({
      modules: () => {
        const highchartsMore = import('highcharts/esm/highcharts-more');

        return [
          highchartsMore,
          highchartsMore.then(
            () => import('highcharts/esm/modules/solid-gauge'),
          ),
        ];
      },
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GaugeChart {
  readonly arcDelimiters = input<number[]>([]);
  readonly arcColors = input<string[]>([]);
  readonly needlePosition = input<number>(0);
  readonly height = input<number>(DEFAULT_HEIGHT);

  protected readonly chartOptions = computed<Options>(() => ({
    credits: { enabled: false },
    title: { text: '' },
    tooltip: { enabled: false },
    chart: {
      type: 'gauge',
      plotBorderWidth: 0,
      plotShadow: false,
      height: this.height(),
      margin: 0,
      backgroundColor: 'transparent',
    },
    pane: {
      startAngle: -90,
      endAngle: 90,
      size: '100%',
      background: [{ backgroundColor: 'transparent', borderWidth: 0 }],
    },
    yAxis: {
      min: 0,
      max: AXIS_MAX,
      labels: { enabled: false },
      tickWidth: 0,
      lineWidth: 0,
      minorTickLength: 0,
      plotBands: this.plotBands(),
    },
    series: [this.chartSeries()],
  }));

  private readonly chartSeries = computed<SeriesGaugeOptions>(() => ({
    type: 'gauge',
    data: [this.needlePosition()],
    dataLabels: {
      format: '{y}',
      borderWidth: 0,
      color: '#333333',
      style: { fontSize: '26px' },
    },
    dial: {
      radius: '80%',
      backgroundColor: 'gray',
      baseWidth: 12,
      baseLength: '0%',
      rearLength: '0%',
    },
    pivot: { backgroundColor: 'gray', radius: 6 },
  }));

  private readonly plotBands = computed<YAxisPlotBandsOptions[]>(() => {
    const delimiters = this.arcDelimiters();
    const colors = this.arcColors();

    return [
      {
        from: 0,
        to: delimiters[0],
        color: colors[0],
        thickness: BAND_THICKNESS,
      },
      ...delimiters.map((delimiter, index) => ({
        from: delimiter,
        to: delimiters[index + 1] || AXIS_MAX,
        color: colors[index + 1] || 'grey',
        thickness: BAND_THICKNESS,
      })),
    ];
  });
}
